import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Percent, Loader2, RotateCcw } from 'lucide-react';
import { API } from '@/App';

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/** Admin control for the default platform share on workshops and live streams. */
export function PlatformFeeDefaultCard({ onChanged }) {
  const [fees, setFees] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/platform-fees`, { headers: authHeader() });
      setFees(data);
      setDraft(String(data.default_percent));
    } catch {
      toast.error('Could not load the platform fee.');
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    const pct = parseFloat(draft);
    if (!Number.isFinite(pct) || pct < 0 || pct > 50) {
      toast.error('Enter a percentage between 0 and 50.');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/platform-fees`, { percent: pct }, { headers: authHeader() });
      toast.success(`Default platform fee is now ${pct}%.`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the platform fee.');
    } finally {
      setSaving(false);
    }
  };

  if (!fees) return null;
  const dirty = String(fees.default_percent) !== draft;

  return (
    <Card className="bg-slate-900/85 border-slate-700" data-testid="platform-fee-default-card">
      <CardContent className="pt-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <p className="text-sm font-semibold text-white flex items-center gap-2">
              <Percent className="w-4 h-4 text-cyan-400" />
              Platform fee on workshops and live streams
            </p>
            <p className="text-xs text-gray-300 mt-1 max-w-xl leading-relaxed">
              Taken from the creator&apos;s earnings. Buyers always pay the listed price plus card
              processing, so changing this never reprices anything for a member, and sales already
              made keep the rate they were sold at.
            </p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-xs text-gray-300 mb-1">Default</label>
              <div className="flex items-center gap-1">
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value.replace(/[^0-9.]/g, '').slice(0, 5))}
                  className="w-20 text-white"
                  inputMode="decimal"
                  data-testid="platform-fee-default-input"
                />
                <span className="text-gray-300">%</span>
              </div>
            </div>
            <Button
              onClick={save}
              disabled={saving || !dirty}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="platform-fee-default-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </div>

        {fees.overrides?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-700" data-testid="platform-fee-overrides">
            <p className="text-xs text-gray-300 mb-2">
              {fees.overrides.length} item{fees.overrides.length === 1 ? '' : 's'} on a custom rate:
            </p>
            <div className="flex flex-wrap gap-2">
              {fees.overrides.map(o => (
                <span
                  key={`${o.kind}-${o.id}`}
                  className="text-xs bg-slate-700/70 text-gray-100 rounded-full px-3 py-1"
                >
                  {o.title} — {o.platform_fee_percent}%
                </span>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Per-item override, shown inside a workshop's detail view. */
export function ItemPlatformFeeEditor({ kind = 'workshop', item, onSaved }) {
  const [draft, setDraft] = useState('');
  const [defaultPct, setDefaultPct] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/platform-fees`, { headers: authHeader() })
      .then(r => {
        setDefaultPct(r.data.default_percent);
        setDraft(String(item?.platform_fee_percent ?? r.data.default_percent));
      })
      .catch(() => {});
  }, [item?.id, item?.platform_fee_percent]);

  const submit = async (percent) => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/platform-fees/${kind === 'workshop' ? 'workshop' : 'stream'}/${item.id}`,
        { percent },
        { headers: authHeader() }
      );
      toast.success(
        percent === null
          ? `Back to the ${data.platform_fee_percent}% default.`
          : `This ${kind === 'workshop' ? 'workshop' : 'stream'} is now on ${data.platform_fee_percent}%.`
      );
      setDraft(String(data.platform_fee_percent));
      onSaved?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the rate.');
    } finally {
      setSaving(false);
    }
  };

  const custom = item?.platform_fee_percent !== undefined && item?.platform_fee_percent !== null;

  return (
    <div className="mt-3 pt-3 border-t border-slate-700" data-testid="item-platform-fee-editor">
      <label className="block text-xs text-gray-300 mb-1">
        Platform fee for this one
        {custom
          ? ' (custom rate)'
          : defaultPct !== null && ` (using the ${defaultPct}% default)`}
      </label>
      <div className="flex items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value.replace(/[^0-9.]/g, '').slice(0, 5))}
          className="w-20 text-white"
          inputMode="decimal"
          data-testid="item-platform-fee-input"
        />
        <span className="text-gray-300 text-sm">%</span>
        <Button
          size="sm"
          onClick={() => submit(parseFloat(draft))}
          disabled={saving || !Number.isFinite(parseFloat(draft))}
          className="bg-cyan-600 hover:bg-cyan-700"
          data-testid="item-platform-fee-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
        </Button>
        {custom && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => submit(null)}
            disabled={saving}
            className="border-slate-600 text-slate-200 hover:text-white hover:bg-slate-700/60"
            data-testid="item-platform-fee-reset"
          >
            <RotateCcw className="w-3 h-3 mr-1" /> Use default
          </Button>
        )}
      </div>
      <p className="text-xs text-gray-400 mt-2">
        Applies to future sales only. Copies already sold keep the rate they were sold at.
      </p>
    </div>
  );
}
